import { useCallback, useEffect, useState } from 'react';
import { api, errorText, formatDate, formatMoney } from '../api';
import { Badge, Card, Empty, Loading, Notice, TextField } from '../components/ui';
import type { Partner } from '../types';

interface Traeger {
  id: string;
  code: string;
  bezeichnung: string;
  pfand_cents: number;
  aktiv: boolean;
}

interface Saldo {
  partner_id: string;
  partner_name: string;
  carrier_id: string;
  code: string;
  bezeichnung: string;
  abgegeben: number;
  erhalten: number;
  saldo: number;
  wert_cents: number;
  letzte_bewegung: string | null;
}

interface Uebersicht {
  offeneKonten: number;
  draussen: number;
  wertCents: number;
  schuldenWir: number;
}

interface Bewegung {
  id: string;
  partner_name: string;
  code: string;
  art: 'abgegeben' | 'erhalten';
  menge: number;
  gebucht_am: string;
  beleg: string | null;
  note: string | null;
  benutzer: string | null;
}

export function CarriersPage() {
  const [traeger, setTraeger] = useState<Traeger[] | null>(null);
  const [salden, setSalden] = useState<Saldo[]>([]);
  const [uebersicht, setUebersicht] = useState<Uebersicht | null>(null);
  const [bewegungen, setBewegungen] = useState<Bewegung[]>([]);
  const [partner, setPartner] = useState<Partner[]>([]);

  const [nurOffene, setNurOffene] = useState(true);
  const [buchung, setBuchung] = useState({
    partnerId: '',
    carrierId: '',
    art: 'abgegeben' as 'abgegeben' | 'erhalten',
    menge: '',
    beleg: '',
    note: '',
  });

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const [typen, konten, verlauf, partnerListe] = await Promise.all([
        api<Traeger[]>('/api/carriers/types'),
        api<{ salden: Saldo[]; uebersicht: Uebersicht }>('/api/carriers/balances', {
          query: { nurOffene: nurOffene ? 'true' : undefined },
        }),
        api<Bewegung[]>('/api/carriers/movements', { query: { limit: '40' } }),
        api<Partner[]>('/api/partners'),
      ]);
      setTraeger(typen);
      setSalden(konten.salden);
      setUebersicht(konten.uebersicht);
      setBewegungen(verlauf);
      setPartner(partnerListe);
    } catch (err) {
      setError(errorText(err));
    }
  }, [nurOffene]);

  useEffect(() => {
    void load();
  }, [load]);

  async function buchen() {
    if (!buchung.partnerId || !buchung.carrierId) {
      return setError('Bitte Partner und Ladungsträger wählen.');
    }
    setBusy(true);
    setError('');
    try {
      const ergebnis = await api<{ saldo: number }>('/api/carriers/movements', {
        method: 'POST',
        body: {
          partnerId: buchung.partnerId,
          carrierId: buchung.carrierId,
          art: buchung.art,
          menge: Math.round(Number(buchung.menge.replace(',', '.')) || 0),
          beleg: buchung.beleg || null,
          note: buchung.note || null,
        },
      });
      setMessage(
        `Gebucht. Neuer Saldo: ${
          ergebnis.saldo > 0
            ? `${ergebnis.saldo} Stück offen zu unseren Gunsten`
            : ergebnis.saldo < 0
              ? `${Math.abs(ergebnis.saldo)} Stück schulden wir`
              : 'ausgeglichen'
        }.`,
      );
      setBuchung({ ...buchung, menge: '', beleg: '', note: '' });
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!traeger || !uebersicht) return error ? <Notice kind="error">{error}</Notice> : <Loading />;

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <div className="grid kpi" style={{ marginBottom: 16 }}>
        <div className={uebersicht.draussen > 0 ? 'kpi-tile alert' : 'kpi-tile'}>
          <div className="label">Bei Partnern</div>
          <div className="value">{uebersicht.draussen.toLocaleString('de-DE')}</div>
          <div className="hint">Stück, die uns zustehen</div>
        </div>
        <div className="kpi-tile">
          <div className="label">Wert davon</div>
          <div className="value">{formatMoney(uebersicht.wertCents)}</div>
          <div className="hint">zum hinterlegten Pfandwert</div>
        </div>
        <div className="kpi-tile">
          <div className="label">Wir schulden</div>
          <div className="value">{uebersicht.schuldenWir.toLocaleString('de-DE')}</div>
          <div className="hint">Stück zurückzugeben</div>
        </div>
        <div className="kpi-tile">
          <div className="label">Offene Konten</div>
          <div className="value">{uebersicht.offeneKonten}</div>
        </div>
      </div>

      <Card title="Bewegung erfassen">
        <div className="small muted" style={{ marginBottom: 12 }}>
          <strong>Abgegeben</strong> heisst: die Träger sind mit unserer Ware hinausgegangen, der
          Partner schuldet sie uns. <strong>Erhalten</strong> heisst: er hat zurückgegeben. Jede
          Buchung bleibt stehen — ein Irrtum wird gegengebucht, nicht gelöscht.
        </div>
        <div className="form-grid">
          <label className="field">
            <span>Partner</span>
            <select
              value={buchung.partnerId}
              onChange={(e) => setBuchung({ ...buchung, partnerId: e.target.value })}
            >
              <option value="">— wählen —</option>
              {partner.map((eintrag) => (
                <option key={eintrag.id} value={eintrag.id}>
                  {eintrag.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Ladungsträger</span>
            <select
              value={buchung.carrierId}
              onChange={(e) => setBuchung({ ...buchung, carrierId: e.target.value })}
            >
              <option value="">— wählen —</option>
              {traeger
                .filter((eintrag) => eintrag.aktiv)
                .map((eintrag) => (
                  <option key={eintrag.id} value={eintrag.id}>
                    {eintrag.code} · {eintrag.bezeichnung}
                  </option>
                ))}
            </select>
          </label>
          <label className="field">
            <span>Richtung</span>
            <select
              value={buchung.art}
              onChange={(e) =>
                setBuchung({ ...buchung, art: e.target.value as 'abgegeben' | 'erhalten' })
              }
            >
              <option value="abgegeben">abgegeben — er schuldet uns</option>
              <option value="erhalten">erhalten — zurückbekommen</option>
            </select>
          </label>
          <TextField
            label="Stück"
            value={buchung.menge}
            onChange={(v) => setBuchung({ ...buchung, menge: v })}
          />
          <TextField
            label="Beleg"
            value={buchung.beleg}
            onChange={(v) => setBuchung({ ...buchung, beleg: v })}
            hint="Lieferschein, Palettenschein"
          />
          <TextField
            label="Vermerk"
            value={buchung.note}
            onChange={(v) => setBuchung({ ...buchung, note: v })}
          />
        </div>
        <button className="primary" onClick={buchen} disabled={busy}>
          {busy ? 'Buchen …' : 'buchen'}
        </button>
      </Card>

      <Card
        title="Konten"
        actions={
          <button className="small" onClick={() => setNurOffene(!nurOffene)}>
            {nurOffene ? 'auch ausgeglichene zeigen' : 'nur offene zeigen'}
          </button>
        }
        padded={false}
      >
        {salden.length === 0 ? (
          <Empty>
            {nurOffene ? 'Alle Konten sind ausgeglichen.' : 'Noch keine Bewegung erfasst.'}
          </Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Partner</th>
                <th>Träger</th>
                <th className="num">Abgegeben</th>
                <th className="num">Erhalten</th>
                <th className="num">Saldo</th>
                <th className="num">Wert</th>
                <th>Zuletzt</th>
              </tr>
            </thead>
            <tbody>
              {salden.map((konto) => (
                <tr key={`${konto.partner_id}-${konto.carrier_id}`}>
                  <td>{konto.partner_name}</td>
                  <td className="small">
                    <span className="mono">{konto.code}</span> {konto.bezeichnung}
                  </td>
                  <td className="num muted">{konto.abgegeben}</td>
                  <td className="num muted">{konto.erhalten}</td>
                  <td className="num">
                    {konto.saldo === 0 ? (
                      <Badge kind="ok">ausgeglichen</Badge>
                    ) : konto.saldo > 0 ? (
                      <Badge kind="warn">{konto.saldo} offen</Badge>
                    ) : (
                      <Badge kind="info">{Math.abs(konto.saldo)} zurück</Badge>
                    )}
                  </td>
                  <td className="num">{konto.saldo > 0 ? formatMoney(konto.wert_cents) : '—'}</td>
                  <td className="small muted">
                    {konto.letzte_bewegung ? formatDate(konto.letzte_bewegung) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Letzte Bewegungen" padded={false}>
        {bewegungen.length === 0 ? (
          <Empty>Noch nichts gebucht.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Datum</th>
                <th>Partner</th>
                <th>Träger</th>
                <th>Richtung</th>
                <th className="num">Stück</th>
                <th>Beleg</th>
                <th>Erfasst von</th>
              </tr>
            </thead>
            <tbody>
              {bewegungen.map((bewegung) => (
                <tr key={bewegung.id}>
                  <td className="small">{formatDate(bewegung.gebucht_am)}</td>
                  <td className="small">{bewegung.partner_name}</td>
                  <td className="mono small">{bewegung.code}</td>
                  <td>
                    <Badge kind={bewegung.art === 'abgegeben' ? 'warn' : 'ok'}>
                      {bewegung.art}
                    </Badge>
                  </td>
                  <td className="num">{bewegung.menge}</td>
                  <td className="small muted">
                    {bewegung.beleg ?? '—'}
                    {bewegung.note ? ` · ${bewegung.note}` : ''}
                  </td>
                  <td className="small muted">{bewegung.benutzer ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
